import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowRoundBack, IoMdEye } from 'react-icons/io';

import PageForm from '../../templates/PageForm';
import SortableColumnHeader from '../monitoring/SortableColumnHeader';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';
import { SortValueType } from '../../../utils/sortUtils';
import AnalysisProgressPanel, {
    AnalysisProgressState,
    initialAnalysisProgress,
} from './AnalysisProgressPanel';
import BatchInfoCard, { BatchSummaryCounts } from './BatchInfoCard';
import TablePaginationFooter from './TablePaginationFooter';
import TableToolbar from './TableToolbar';
import useClientTable from './hooks/useClientTable';
import {
    RecommendationState,
    getRecommendationBadgeClass,
    getRecommendationLabel,
    normalizeRecommendation,
} from './utils/statusLabels';

interface DocumentRecommendation {
    document: string;
    entityName: string;
    entityType: 'CPF' | 'CNPJ';
    recommendation: RecommendationState;
    creditFlag: boolean;
    triggeredRules: string[];
    provider: string;
    rawData: any;
}

const COLUMN_TYPES: Record<string, SortValueType> = {
    document: 'text',
    entityName: 'text',
    entityType: 'text',
    recommendation: 'text',
    creditFlag: 'number',
    triggeredRules: 'number',
};

const RECOMMENDATION_FILTER_OPTIONS = [
    { value: '', label: 'Todas' },
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'REJECTED', label: 'Reprovado' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'ERROR', label: 'Erro na análise' },
    { value: 'NO_DATA', label: 'Sem dados' },
    { value: 'PROCESSING', label: 'Processando' },
];

const CREDIT_FILTER_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' },
];

/**
 * AF-3418: consolida os reports por documento.
 *
 * Prioridade da recomendação: REJECTED > ERROR > PENDING > APPROVED. ERROR precisa
 * sobrepor PENDING/APPROVED para não esconder que parte da análise não foi concluída;
 * REPROVADO permanece por ser uma decisão firme.
 */
const consolidateReports = (reports: any[], provider: string): DocumentRecommendation[] => {
    const documentMap = new Map<string, DocumentRecommendation>();

    reports.forEach((report: any) => {
        const document = report.document || report.social_id || 'N/A';
        const entityType =
            report.backend_document_type ||
            (report.document && report.document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ');

        // Report ainda sem análise aplicada aparece como "Processando" em vez de erro:
        // durante o carregamento progressivo isso é o estado normal, não uma falha.
        const recommendation: RecommendationState = report.backend_recommendation
            ? normalizeRecommendation(report.backend_recommendation)
            : 'PROCESSING';

        const existing = documentMap.get(document);
        if (!existing) {
            documentMap.set(document, {
                document,
                entityName: report.entity_name || report.name || report.nome || '',
                entityType,
                recommendation,
                creditFlag: report.backend_credit_flag || false,
                triggeredRules: report.backend_reasons || [],
                provider,
                rawData: report,
            });
            return;
        }

        if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
            const merged = new Set(existing.triggeredRules);
            report.backend_reasons.forEach((rule: string) => merged.add(rule));
            existing.triggeredRules = Array.from(merged);
        }

        // AF-3460: prioridade REJECTED > ERROR > PENDING > NO_DATA > APPROVED.
        // NO_DATA fica acima de APPROVED porque, se um report do documento não tinha dado,
        // isso é informação que o analista precisa ver — exibir "Aprovado" a esconderia.
        // Mas fica abaixo de PENDING/REJECTED/ERROR, que são mais fortes.
        if (recommendation === 'REJECTED') {
            existing.recommendation = 'REJECTED';
        } else if (recommendation === 'ERROR' && existing.recommendation !== 'REJECTED') {
            existing.recommendation = 'ERROR';
        } else if (
            recommendation === 'PENDING' &&
            existing.recommendation !== 'REJECTED' &&
            existing.recommendation !== 'ERROR'
        ) {
            existing.recommendation = 'PENDING';
        } else if (
            recommendation === 'NO_DATA' &&
            existing.recommendation === 'APPROVED'
        ) {
            existing.recommendation = 'NO_DATA';
        } else if (existing.recommendation === 'PROCESSING' && recommendation !== 'PROCESSING') {
            existing.recommendation = recommendation;
        }

        if (report.backend_credit_flag) {
            existing.creditFlag = true;
        }

        if (!existing.entityName) {
            existing.entityName = report.entity_name || report.name || report.nome || '';
        }
    });

    return Array.from(documentMap.values());
};

const RecommendationEngineRecommendationView: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();

    const cachedBatchData = location.state?.batchData;
    const [batchData, setBatchData] = useState<any>(cachedBatchData || null);
    // AF-3418: reports vistos até agora. Alimentado pelas páginas conforme chegam, para a
    // tabela existir enquanto o lote ainda está sendo carregado.
    const [streamedReports, setStreamedReports] = useState<any[]>(
        cachedBatchData?.reports || []
    );
    const [batchMeta, setBatchMeta] = useState<{
        provider?: string;
        identification?: string;
        status?: string;
        createdAt?: string;
    }>({
        provider: cachedBatchData?.provider,
        identification: cachedBatchData?.identification,
        status: cachedBatchData?.status,
        createdAt: cachedBatchData?.createdAt,
    });
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<AnalysisProgressState>(initialAnalysisProgress);

    const [recommendationFilter, setRecommendationFilter] = useState('');
    const [creditFilter, setCreditFilter] = useState('');

    const loadBatchDetails = useCallback(async () => {
        if (!id) {
            setError('ID do lote não informado');
            return;
        }

        setError(null);
        setStreamedReports([]);
        setProgress({ ...initialAnalysisProgress, stage: 'loading-reports' });

        try {
            const data = await recommendationBFFService.analyzeBatch(
                id,
                (processed, total) => {
                    setProgress((prev) => ({
                        ...prev,
                        stage: 'applying-rules',
                        analyzed: processed,
                        reportsTotal: prev.reportsTotal ?? total,
                    }));
                },
                {
                    onReportsPage: ({ reports, loaded, total }) => {
                        // Renderiza as linhas já disponíveis. A ordem definitiva de
                        // batchData.reports é montada por número de página dentro do BFF —
                        // aqui só acumulamos para exibição.
                        setStreamedReports((prev) => [...prev, ...reports]);
                        setProgress((prev) => ({
                            ...prev,
                            stage: 'loading-reports',
                            reportsLoaded: loaded,
                            reportsTotal: total ?? prev.reportsTotal,
                        }));
                    },
                    onPartialResults: (reports) => {
                        setStreamedReports([...reports]);
                    },
                }
            );

            setProgress((prev) => ({ ...prev, stage: 'consolidating' }));

            setBatchData(data);
            setStreamedReports(data.reports || []);
            setBatchMeta({
                provider: data.provider,
                identification: data.identification,
                status: data.status,
                createdAt: data.createdAt,
            });
            setProgress((prev) => ({ ...prev, stage: 'done' }));
        } catch (err: any) {
            const message = err?.message || 'Erro ao carregar dados do lote';
            setError(message);
            setProgress((prev) => ({ ...prev, stage: 'error', errorMessage: message }));
        }
    }, [id]);

    useEffect(() => {
        if (!id) return;
        if (cachedBatchData) {
            setBatchData(cachedBatchData);
            setStreamedReports(cachedBatchData.reports || []);
            setProgress({ ...initialAnalysisProgress, stage: 'done' });
            return;
        }
        loadBatchDetails();
        // cachedBatchData vem do state de navegação e não muda durante a vida da tela
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const recommendations = useMemo(
        () => consolidateReports(streamedReports, batchMeta.provider || 'UNKNOWN'),
        [streamedReports, batchMeta.provider]
    );

    const summary = useMemo<BatchSummaryCounts>(() => {
        const counts: BatchSummaryCounts = {
            total: recommendations.length,
            approved: 0,
            rejected: 0,
            pending: 0,
            creditFlagged: 0,
            errors: 0,
            noData: 0,
        };
        recommendations.forEach((rec) => {
            if (rec.recommendation === 'APPROVED') counts.approved += 1;
            else if (rec.recommendation === 'REJECTED') counts.rejected += 1;
            else if (rec.recommendation === 'PENDING') counts.pending += 1;
            else if (rec.recommendation === 'ERROR') counts.errors += 1;
            else if (rec.recommendation === 'NO_DATA') counts.noData += 1;
            if (rec.creditFlag) counts.creditFlagged += 1;
        });
        return counts;
    }, [recommendations]);

    const filterFn = useCallback(
        (row: DocumentRecommendation) => {
            if (recommendationFilter && row.recommendation !== recommendationFilter) return false;
            if (creditFilter === 'true' && !row.creditFlag) return false;
            if (creditFilter === 'false' && row.creditFlag) return false;
            return true;
        },
        [recommendationFilter, creditFilter]
    );

    const searchableFields = useCallback(
        (row: DocumentRecommendation) => [row.document, row.entityName],
        []
    );

    const valueAccessor = useCallback((row: DocumentRecommendation, field: string) => {
        if (field === 'creditFlag') return row.creditFlag ? 1 : 0;
        if (field === 'triggeredRules') return row.triggeredRules.length;
        if (field === 'recommendation') return getRecommendationLabel(row.recommendation);
        return (row as unknown as Record<string, unknown>)[field];
    }, []);

    const table = useClientTable<DocumentRecommendation>({
        rows: recommendations,
        columnTypes: COLUMN_TYPES,
        valueAccessor,
        searchableFields,
        filterFn,
        initialPageSize: 25,
    });

    const hasActiveFilters = Boolean(table.search || recommendationFilter || creditFilter);

    const clearFilters = () => {
        table.setSearch('');
        setRecommendationFilter('');
        setCreditFilter('');
    };

    const viewDocumentDetails = (recommendation: DocumentRecommendation) => {
        navigate(`/recommendation-engine/provider-documents/${id}`, {
            state: {
                document: recommendation.document,
                recommendation,
                batchData,
            },
        });
    };

    const handleBack = () => {
        navigate('/recommendation-engine/recommendations/0/20/createdAt/DESC');
    };

    const isLoading = progress.stage !== 'done' && progress.stage !== 'error' && progress.stage !== 'idle';

    return (
        <PageForm
            pageTitle={`Recomendações PLD - ${batchMeta.identification || `Lote ${id}`}`}
            apiVersion="1.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Recommendation Engine', url: '/recommendation-engine' },
                { label: 'Recomendações', url: '#' },
            ]}
            controlSlot={<></>}
        >
            <div className="space-y-4">
                <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-2 rounded-md bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
                >
                    <IoMdArrowRoundBack />
                    Voltar para Lista de Lotes
                </button>

                <BatchInfoCard
                    provider={batchMeta.provider}
                    identification={batchMeta.identification}
                    status={batchMeta.status}
                    createdAt={batchMeta.createdAt}
                    summary={summary}
                />

                <AnalysisProgressPanel progress={progress} />

                {error && progress.stage === 'error' && (
                    <button
                        onClick={loadBatchDetails}
                        className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                        Tentar novamente
                    </button>
                )}

                <div>
                    <TableToolbar
                        search={table.search}
                        onSearchChange={table.setSearch}
                        searchPlaceholder="Buscar por documento ou nome"
                        selects={[
                            {
                                id: 'recommendation',
                                label: 'Recomendação',
                                value: recommendationFilter,
                                options: RECOMMENDATION_FILTER_OPTIONS,
                                className: 'w-52',
                                onChange: (value) => {
                                    setRecommendationFilter(value);
                                    table.setPage(1);
                                },
                            },
                            {
                                id: 'creditFlag',
                                label: 'Sinalizado crédito',
                                value: creditFilter,
                                options: CREDIT_FILTER_OPTIONS,
                                // Rótulo longo: precisa caber numa linha sobre a borda
                                className: 'w-52',
                                onChange: (value) => {
                                    setCreditFilter(value);
                                    table.setPage(1);
                                },
                            },
                        ]}
                        filteredCount={table.totalRows}
                        totalCount={table.unfilteredTotal}
                        onClear={clearFilters}
                        hasActiveFilters={hasActiveFilters}
                    />

                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                            <thead className="bg-gray-50 dark:bg-slate-700">
                                <tr>
                                    <SortableColumnHeader
                                        label="Documento"
                                        field="document"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Nome/Razão Social"
                                        field="entityName"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Tipo"
                                        field="entityType"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Recomendação"
                                        field="recommendation"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Sinalizado Crédito"
                                        field="creditFlag"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <SortableColumnHeader
                                        label="Regras Ativadas"
                                        field="triggeredRules"
                                        currentSortBy={table.sortBy}
                                        currentSortOrder={table.sortOrder}
                                        onSort={table.toggleSort}
                                    />
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white dark:divide-slate-700 dark:bg-slate-800">
                                {table.pageRows.map((rec) => (
                                    <tr
                                        key={rec.document}
                                        className="hover:bg-gray-50 dark:hover:bg-slate-700"
                                    >
                                        <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {rec.document}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                                            {rec.entityName || (
                                                <span className="text-gray-400">Nome não disponível</span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                                            {rec.entityType === 'CPF' ? 'PF' : 'PJ'}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span
                                                className={getRecommendationBadgeClass(rec.recommendation)}
                                                title={rec.rawData?.backend_error || undefined}
                                            >
                                                {getRecommendationLabel(rec.recommendation)}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {rec.creditFlag ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                    Sim
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-400">Não</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {rec.triggeredRules.length > 0 ? (
                                                <ul className="max-w-xs space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                                                    {rec.triggeredRules.slice(0, 3).map((rule, idx) => (
                                                        <li key={idx} className="truncate" title={rule}>
                                                            • {rule}
                                                        </li>
                                                    ))}
                                                    {rec.triggeredRules.length > 3 && (
                                                        <li className="text-gray-400">
                                                            +{rec.triggeredRules.length - 3} outras
                                                        </li>
                                                    )}
                                                </ul>
                                            ) : (
                                                <span className="text-xs text-gray-400">
                                                    Nenhuma regra ativada
                                                </span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <button
                                                onClick={() => viewDocumentDetails(rec)}
                                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                                title="Ver detalhes"
                                            >
                                                <IoMdEye size={16} />
                                                Detalhes
                                            </button>
                                        </td>
                                    </tr>
                                ))}

                                {table.pageRows.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                                        >
                                            {isLoading
                                                ? 'Carregando documentos do lote...'
                                                : hasActiveFilters
                                                  ? 'Nenhum documento encontrado com os filtros aplicados'
                                                  : 'Nenhum documento neste lote'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <TablePaginationFooter
                        page={table.page}
                        totalPages={table.totalPages}
                        onPageChange={table.setPage}
                        pageSize={table.pageSize}
                        onPageSizeChange={table.setPageSize}
                        rangeStart={table.rangeStart}
                        rangeEnd={table.rangeEnd}
                        totalRows={table.totalRows}
                    />
                </div>
            </div>
        </PageForm>
    );
};

export default RecommendationEngineRecommendationView;
